// src/components/hospitalizacion/AccesosInvasivosCard.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  FormControlLabel,
  Checkbox,
  Divider,
  Stack
} from '@mui/material';
import { SettingsInputComponent, WarningAmber, Block } from '@mui/icons-material';
import api from '../../api/api';
import dayjs from 'dayjs';

const AccesosInvasivosCard = ({ hospitalizacionId, variant = 'full' }) => {
  const [accesos, setAccesos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRetirados, setShowRetirados] = useState(false);

  // Estados para modal de retiro
  const [openRetiroModal, setOpenRetiroModal] = useState(false);
  const [accesoSeleccionado, setAccesoSeleccionado] = useState(null);
  const [motivoRetiro, setMotivoRetiro] = useState('');
  const [complicacionRetiro, setComplicacionRetiro] = useState('');
  const [errorRetiro, setErrorRetiro] = useState('');

  useEffect(() => {
    if (hospitalizacionId) fetchAccesos();
  }, [hospitalizacionId, showRetirados]);

  const fetchAccesos = async () => {
    try {
      setLoading(true);
      setError('');

      let url = `/accesos-invasivos/activos/${hospitalizacionId}`;
      if (showRetirados) {
        url += '?include_retirados=true';
      }

      const res = await api.get(url);
      setAccesos(res.data);
    } catch (err) {
      console.error('Error cargando accesos:', err);
      setError('No se pudieron cargar los accesos invasivos');
      setAccesos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAbrirRetiro = (acceso) => {
    setAccesoSeleccionado(acceso);
    setMotivoRetiro('');
    setComplicacionRetiro('');
    setErrorRetiro('');
    setOpenRetiroModal(true);
  };

  const handleCerrarRetiro = () => {
    setOpenRetiroModal(false);
    setAccesoSeleccionado(null);
  };

  const handleConfirmarRetiro = async () => {
    if (!accesoSeleccionado) return;

    if (!motivoRetiro.trim()) {
      setErrorRetiro('Es recomendable indicar el motivo del retiro');
      return;
    }

    try {
      const url = `/accesos-invasivos/${accesoSeleccionado.id_acceso}/retirar?` +
        `motivo=${encodeURIComponent(motivoRetiro.trim())}` +
        (complicacionRetiro.trim() ? `&complicacion=${encodeURIComponent(complicacionRetiro.trim())}` : '');

      await api.put(url);

      setOpenRetiroModal(false);
      fetchAccesos();
    } catch (err) {
      console.error('Error al retirar acceso:', err);
      setErrorRetiro('No se pudo retirar el acceso. Intenta de nuevo.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  // Versión compacta (cabecera)
  if (variant === 'compact') {
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
        {accesos.length === 0 ? (
          <Chip label="Sin accesos invasivos" size="small" />
        ) : (
          <>
            {accesos.some(ac => ac.estado !== 'retirado' && dayjs().diff(dayjs(ac.fecha_instalacion), 'hour') > 72) && (
              <Chip label="¡Acceso >72h!" color="error" icon={<WarningAmber />} size="small" />
            )}
            {accesos.map(ac => {
              const horas = dayjs().diff(dayjs(ac.fecha_instalacion), 'hour');
              const color = ac.estado === 'retirado' ? 'default' : (horas > 72 ? 'error' : 'default');
              return (
                <Tooltip key={ac.id_acceso} title={`${ac.tipo_dispositivo} - ${horas}h - ${ac.estado.toUpperCase()}`}>
                  <Chip
                    label={`${ac.calibre || 'N/A'} - ${ac.estado}`}
                    size="small"
                    color={color}
                    icon={<SettingsInputComponent />}
                    sx={{ textDecoration: ac.estado === 'retirado' ? 'line-through' : 'none' }}
                  />
                </Tooltip>
              );
            })}
          </>
        )}
      </Stack>
    );
  }

  // Versión completa
  return (
    <>
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
        <Box sx={{ p: 2, bgcolor: '#1e293b', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsInputComponent sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              ACCESOS INVASIVOS ({accesos.length})
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={showRetirados}
                onChange={(e) => setShowRetirados(e.target.checked)}
                size="small"
                sx={{ color: 'white' }}
              />
            }
            label={<Typography variant="body2" sx={{ color: 'white' }}>Mostrar retirados</Typography>}
          />
        </Box>

        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {accesos.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No hay accesos invasivos {showRetirados ? '' : 'activos'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ p: 2 }}>
            {accesos.map(ac => {
              const horas = dayjs().diff(dayjs(ac.fecha_instalacion), 'hour');
              const alerta = ac.estado !== 'retirado' && horas > 72;
              const esRetirado = ac.estado === 'retirado';

              return (
                <Grid item xs={12} sm={6} md={4} key={ac.id_acceso}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: esRetirado ? '#f1f5f9' : (alerta ? '#fef2f2' : '#f8fafc'),
                      border: esRetirado ? '1px dashed #9ca3af' : (alerta ? '2px solid #ef4444' : '1px solid #e2e8f0'),
                      borderRadius: 2,
                      position: 'relative',
                      opacity: esRetirado ? 0.8 : 1,
                    }}
                  >
                    {alerta && !esRetirado && (
                      <Tooltip title="¡Acceso >72h! Considerar retiro">
                        <WarningAmber sx={{ position: 'absolute', top: 8, right: 8, color: '#ef4444' }} />
                      </Tooltip>
                    )}

                    <Typography variant="subtitle2" fontWeight="bold" sx={{ textDecoration: esRetirado ? 'line-through' : 'none' }}>
                      {ac.tipo_dispositivo}
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Calibre: <strong>{ac.calibre || 'N/A'}</strong>
                    </Typography>

                    {/* AÑADIMOS EL NOMBRE DEL RESPONSABLE AQUÍ */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                          <Box sx={{ 
                            width: 20, height: 20, bgcolor: '#1e293b', color: 'white', 
                            borderRadius: '50%', fontSize: '0.6rem', display: 'flex', 
                            alignItems: 'center', justifyContent: 'center' 
                          }}>
                            {ac.usuario_instalador?.substring(0, 1).toUpperCase() || 'S'}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Instalado por: <strong>{ac.usuario_instalador || 'Sistema'}</strong>
                          </Typography>
                        </Box>

                    <Typography variant="caption" color="text.secondary">
                      {ac.sitio_anatomico} {ac.lado && `(${ac.lado})`}
                    </Typography>

                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip
                        label={ac.estado.toUpperCase()}
                        size="small"
                        color={
                          esRetirado ? 'default' :
                          alerta ? 'error' :
                          ac.estado === 'activo' ? 'success' : 'warning'
                        }
                      />
                      <Typography variant="caption" color={alerta && !esRetirado ? 'error' : 'text.secondary'}>
                        {horas}h
                        {esRetirado && ` • Retirado ${dayjs(ac.fecha_retiro_real).format('hh:mm A')}`}  
                      </Typography>
                    </Box>

                    {!esRetirado && (
                      <Box sx={{ mt: 1.5 }}>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<Block />}
                          fullWidth
                          onClick={() => handleAbrirRetiro(ac)}
                        >
                          Retirar
                        </Button>
                      </Box>
                    )}

                    {/* Información adicional solo para retirados */}
                    {esRetirado && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #d1d5db' }}>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                          Motivo del retiro:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {ac.observaciones?.includes('Retiro:') 
                            ? ac.observaciones.split('Retiro:')[1]?.trim() || 'No especificado'
                            : 'No especificado'}
                        </Typography>

                        {ac.complicacion && (
                          <>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Complicación observada:
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              {ac.complicacion}
                            </Typography>
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Card>

      {/* Modal de retiro */}
      <Dialog open={openRetiroModal} onClose={handleCerrarRetiro} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#ef4444', color: 'white' }}>
          Retirar Acceso Invasivo
        </DialogTitle>

        <DialogContent dividers>
          {errorRetiro && <Alert severity="error" sx={{ mb: 2 }}>{errorRetiro}</Alert>}

          <Typography variant="body1" gutterBottom sx={{ mt: 1 }}>
            ¿Confirmas el retiro de <strong>{accesoSeleccionado?.tipo_dispositivo}</strong>?
          </Typography>

          <TextField
            label="Motivo del retiro *"
            fullWidth
            multiline
            rows={2}
            value={motivoRetiro}
            onChange={(e) => setMotivoRetiro(e.target.value)}
            placeholder="Ej: Cambio programado, flebitis, fin de tratamiento, obstrucción, extracción accidental..."
            required
            sx={{ mt: 2 }}
          />

          <TextField
            label="Complicación observada (opcional)"
            fullWidth
            multiline
            rows={2}
            value={complicacionRetiro}
            onChange={(e) => setComplicacionRetiro(e.target.value)}
            placeholder="Ej: Flebitis grado 2, infiltración, trombosis sospechada, sangrado en sitio..."
            sx={{ mt: 2 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCerrarRetiro} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarRetiro}
            variant="contained"
            color="error"
            disabled={loading || !motivoRetiro.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <Block />}
          >
            Confirmar Retiro
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccesosInvasivosCard;